import { useShow } from '@/contexts/ShowContext'
import { useEpisode } from '@/contexts/EpisodeContext'
import RichTextEditor from '@/components/text-editor/RichTextEditor'
import AssetsEditor from '@/components/AssetsEditor'
import { ScriptAssetsProvider } from '@/contexts/ScriptAssetsContext'
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/shadcn/resizable'
import Loader from '@/components/loader'
import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { apiFetch } from '@/lib/api'

export default function EpisodeScriptPage() {
    const {show} = useShow()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [assetsVisible, setAssetsVisible] = useState(false)
    const [assetsFolder, setAssetsFolder] = useState(null)
    // When a script chip asks to generate, we reveal the panel and hand the
    // preset (model + payload props) to the AssetsEditor. The `nonce` makes
    // each request re-trigger even when the same preset is sent twice.
    const [presetGeneration, setPresetGeneration] = useState(null)
    const {episodeId} = useParams()
    const {episode, updateEpisode, updateEpisodeField} = useEpisode()

    const requestGeneration = useCallback((preset) => {
        setAssetsVisible(true)
        setPresetGeneration({ ...preset, nonce: Date.now() })
    }, [])

    const getData = async () => {
        try{
            const response = await apiFetch(`/api/shows/${show._id}/episodes/${episodeId}`)
            if(!response.ok){
                throw new Error('Failed to fetch segment')
            }
            const data = await response.json()
            updateEpisode(data)
        }
        catch(error){
            console.error('Error fetching segment:', error)
            setError(error.message)
        }
    }

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            await getData()
            setLoading(false)
        }
        load()
    }, [episodeId])

    if (loading  || !episode) return (<Loader />)
    if (error) return (<div className="absolute top-0 left-0 w-full h-full flex items-center justify-center"><span className="text-red-400">Error: {error}</span></div>)

    return (
        <ScriptAssetsProvider destinationFolder={assetsFolder} requestGeneration={requestGeneration}>
            <div className="flex flex-1 h-full relative">
                <ResizablePanelGroup direction="horizontal">
                    <ResizablePanel minSize={30}>
                        <RichTextEditor
                            initialValue={episode?.script || ''}
                            onChange={(text) => updateEpisodeField('script', text)}
                            characters={show.characters}
                            placeholder="Write your script..."
                            showAssetsToggle
                            assetsVisible={assetsVisible}
                            onToggleAssets={() => setAssetsVisible(v => !v)}
                        />
                    </ResizablePanel>
                    {assetsVisible && (
                        <>
                            <ResizableHandle className="outline-none" />
                            <ResizablePanel defaultSize={600} minSize={350}>
                                <div className="flex flex-col h-full overflow-hidden border-l border-border">
                                    <AssetsEditor
                                        folder={assetsFolder}
                                        onFolderChange={setAssetsFolder}
                                        presetGeneration={presetGeneration}
                                    />
                                </div>
                            </ResizablePanel>
                        </>
                    )}
                </ResizablePanelGroup>
            </div>
        </ScriptAssetsProvider>
    )
}
